import PropTypes from 'prop-types';
import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card, CardBody, CardHeader, Col, Row } from 'reactstrap';
import Chart from 'react-apexcharts';
import { EmptyState } from '../../Components/Common/ListStates';
import { usePermission } from '../../contexts/PermissionContext';
import {
  ActivityFeed,
  AlertPanel,
  DashboardError,
  DashboardHeader,
  KpiCard,
  KpiSkeleton,
  LimitedNotice,
  QuickActions,
} from './DashboardParts';
import useDashboardData from './useDashboardData';
import {
  STAGES,
  computeTrend,
  deadlineInfo,
  loadLegacyOverview,
  percentOf,
  stageBadgeColor,
  stageLabel,
  timeAgo,
} from './dashboardUtils';
import StatusBadge, { colorToTone } from '../../Components/Common/StatusBadge';

const QUICK_ACTIONS = [
  {
    label: 'Procurements',
    icon: 'ri-file-list-3-line',
    to: '/apps-procurement/view',
  },
  {
    label: 'Tender opening',
    icon: 'ri-door-open-line',
    to: '/apps-tenderopening/view',
  },
  {
    label: 'Tender evaluation',
    icon: 'ri-scales-3-line',
    to: '/apps-tenderevaluation/view',
  },
  { label: 'Awarding', icon: 'ri-trophy-line', to: '/apps-awarding/view' },
];

const PipelineCard = ({ overview }) => {
  const navigate = useNavigate();
  const { can, isMenuLoading } = usePermission();
  const counts = Object.fromEntries(
    overview.pipeline.map(p => [p.name, p.count])
  );
  const values = STAGES.map(s => counts[s.key] || 0);
  const hasData = values.some(Boolean);
  const showPipeline = !overview.limited;
  const maxProduct = Math.max(1, ...overview.byProduct.map(p => p.count));

  const options = {
    chart: {
      type: 'bar',
      toolbar: { show: false },
      events: {
        dataPointSelection: (_e, _c, { dataPointIndex }) => {
          const stage = STAGES[dataPointIndex];
          if (stage && !isMenuLoading && can('canRead', stage.link)) {
            navigate(stage.link);
          }
        },
      },
    },
    plotOptions: {
      bar: {
        horizontal: true,
        distributed: true,
        borderRadius: 4,
        barHeight: '60%',
      },
    },
    colors: STAGES.map(s => s.color),
    dataLabels: { enabled: true, style: { fontSize: '12px', fontWeight: 600 } },
    legend: { show: false },
    xaxis: {
      categories: STAGES.map(s => s.label),
      labels: { show: false },
      axisBorder: { show: false },
      axisTicks: { show: false },
    },
    yaxis: { labels: { style: { fontSize: '12px' } } },
    grid: { show: false, padding: { left: 0, right: 8 } },
    tooltip: { y: { formatter: v => `${v} procurement${v === 1 ? '' : 's'}` } },
  };

  return (
    <Card className='h-100 mb-0'>
      <CardHeader className='bg-transparent'>
        <h5 className='card-title mb-0'>
          {showPipeline ? 'Procurement pipeline' : 'Procurement by product'}
        </h5>
        <p className='text-muted fs-13 mb-0'>
          {showPipeline
            ? 'Where every procurement stands today.'
            : 'Share of all procurements per product.'}
        </p>
      </CardHeader>
      <CardBody>
        {showPipeline &&
          (hasData ? (
            <div
              role='img'
              aria-label={`Bar chart of procurements per stage: ${STAGES.map(
                (s, i) => `${s.label} ${values[i]}`
              ).join(', ')}.`}
            >
              <Chart
                options={options}
                series={[{ name: 'Procurements', data: values }]}
                type='bar'
                height={230}
              />
            </div>
          ) : (
            <p className='text-muted mb-0' role='status'>
              No procurements yet.
            </p>
          ))}

        {!showPipeline && overview.byProduct.length === 0 && (
          <p className='text-muted mb-0' role='status'>
            No procurements yet.
          </p>
        )}

        {overview.byProduct.length > 0 && (
          <div className={showPipeline ? 'mt-3 pt-3 border-top' : ''}>
            <h6 className='fs-13 text-muted text-uppercase mb-3'>By product</h6>
            {overview.byProduct.map(p => (
              <div key={p.name} className='mb-2'>
                <div className='d-flex justify-content-between fs-13 mb-1'>
                  <span className='text-truncate me-2'>{p.name}</span>
                  <span className='fw-medium'>
                    {p.count}
                    <span className='text-muted'>
                      {' '}
                      ({percentOf(p.count, overview.total)}%)
                    </span>
                  </span>
                </div>
                <div className='progress progress-sm'>
                  <div
                    className='progress-bar bg-primary'
                    role='progressbar'
                    style={{ width: `${(p.count / maxProduct) * 100}%` }}
                    aria-label={`${p.name}: ${p.count}`}
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </CardBody>
    </Card>
  );
};

PipelineCard.propTypes = { overview: PropTypes.object.isRequired };

const RecentProcurements = ({ items }) => {
  const navigate = useNavigate();
  const { can, isMenuLoading } = usePermission();
  const canView = !isMenuLoading && can('canRead', '/apps-procurement/view');

  return (
    <Card className='h-100 mb-0'>
      <CardHeader className='bg-transparent d-flex align-items-center'>
        <h5 className='card-title mb-0 flex-grow-1'>Recently updated</h5>
        {canView && (
          <Link to='/apps-procurement/view' className='fs-13'>
            View all
          </Link>
        )}
      </CardHeader>
      <CardBody className='p-0'>
        {items.length === 0 ? (
          <EmptyState
            compact
            title='No procurements yet'
            emptyHint='Procurements will appear here as soon as the first one is created.'
            action={
              canView
                ? {
                    label: 'Go to Procurements',
                    icon: 'ri-arrow-right-line',
                    onClick: () => navigate('/apps-procurement/view'),
                  }
                : null
            }
          />
        ) : (
          <div className='table-responsive'>
            <table
              className='table table-hover align-middle mb-0'
              aria-label='Recently updated procurements'
            >
              <thead className='table-light'>
                <tr>
                  <th scope='col'>Procurement</th>
                  <th scope='col'>Stage</th>
                  <th scope='col'>Submission deadline</th>
                  <th scope='col'>Updated</th>
                </tr>
              </thead>
              <tbody>
                {items.map(item => {
                  const showDeadline = item.stage === 'tender';
                  const deadline = deadlineInfo(item.deadline);
                  const label = item.procurementNumber || 'Untitled';
                  return (
                    <tr key={item.id}>
                      <td className='dashboard-cell-title'>
                        {canView ? (
                          <Link
                            to={`/apps-procurement/upd/${item.id}`}
                            className='fw-medium'
                          >
                            {label}
                          </Link>
                        ) : (
                          <span className='fw-medium'>{label}</span>
                        )}
                        <div className='text-muted fs-12 text-truncate'>
                          {[item.title, item.productName]
                            .filter(Boolean)
                            .join(' · ')}
                        </div>
                      </td>
                      <td>
                        <StatusBadge
                          tone={colorToTone(stageBadgeColor(item.stage))}
                          label={stageLabel(item.stage)}
                        />
                      </td>
                      <td>
                        {showDeadline ? (
                          <span
                            className={
                              deadline.tone === 'muted'
                                ? 'text-muted'
                                : `text-${deadline.tone}`
                            }
                          >
                            {deadline.label}
                          </span>
                        ) : (
                          <span className='text-muted'>-</span>
                        )}
                      </td>
                      <td className='text-muted'>
                        {timeAgo(item.lastActivity)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </CardBody>
    </Card>
  );
};

RecentProcurements.propTypes = { items: PropTypes.array.isRequired };

const StaffDashboard = ({ token, user }) => {
  const { data, loading, error, forbidden, updatedAt, reload } =
    useDashboardData('overview', token, loadLegacyOverview);

  if (forbidden) {
    return (
      <EmptyState
        icon='ri-lock-2-line'
        title='No dashboard for your role'
        emptyHint='Your role does not include procurement data. Use the menu to open the pages you can access.'
      />
    );
  }

  const header = (
    <DashboardHeader
      user={user}
      subtitle='Here is what needs your attention across procurements.'
      updatedAt={updatedAt}
      onRefresh={reload}
      loading={loading}
    >
      <QuickActions actions={QUICK_ACTIONS} />
    </DashboardHeader>
  );

  if (error && !data) {
    return (
      <>
        {header}
        <DashboardError message={error} onRetry={reload} />
      </>
    );
  }

  if (!data) {
    return (
      <>
        {header}
        <KpiSkeleton />
      </>
    );
  }

  const overview = {
    ...data,
    pipeline: data.pipeline || [],
    byProduct: data.byProduct || [],
    alerts: data.alerts || [],
    recentProcurements: data.recentProcurements || [],
    recentActivities: data.recentActivities || [],
  };

  const { limited } = overview;

  return (
    <>
      {header}

      {limited && (
        <LimitedNotice sections='the pipeline, monthly trend, overdue alerts, recent procurements and your activity' />
      )}

      <Row className='g-3 mb-3'>
        <Col xl={3} sm={6}>
          <KpiCard
            label='Total procurements'
            value={overview.total}
            icon='ri-stack-line'
            trend={
              limited
                ? null
                : computeTrend(overview.newThisMonth, overview.newLastMonth)
            }
            hint={`${overview.draft || 0} still in draft`}
            to='/apps-procurement/view'
          />
        </Col>
        <Col xl={3} sm={6}>
          <KpiCard
            label='In progress'
            value={overview.inProgress}
            icon='ri-loader-4-line'
            color='info'
            hint='HPS request, tender and evaluation'
            to='/apps-tenderopening/view'
          />
        </Col>
        <Col xl={3} sm={6}>
          <KpiCard
            label='Waiting for evaluation'
            value={overview.evaluationQueue}
            icon='ri-scales-3-line'
            color='warning'
            emphasis={overview.evaluationQueue > 0}
            hint={`${overview.underEvaluation} under evaluation now`}
            to='/apps-tenderevaluation/view'
          />
        </Col>
        <Col xl={3} sm={6}>
          <KpiCard
            label='Awarded'
            value={overview.awarded}
            icon='ri-trophy-line'
            color='success'
            hint={`${percentOf(overview.awarded, overview.total)}% of all procurements`}
            to='/apps-awarding/view'
          />
        </Col>
      </Row>

      <Row className='g-3 mb-3'>
        <Col xl={7}>
          <AlertPanel alerts={overview.alerts} />
        </Col>
        <Col xl={5}>
          <PipelineCard overview={overview} />
        </Col>
      </Row>

      {!limited && (
        <Row className='g-3'>
          <Col xl={8}>
            <RecentProcurements items={overview.recentProcurements} />
          </Col>
          <Col xl={4}>
            <ActivityFeed items={overview.recentActivities} />
          </Col>
        </Row>
      )}
    </>
  );
};

StaffDashboard.propTypes = {
  token: PropTypes.string,
  user: PropTypes.object,
};

export default StaffDashboard;
